import { api, acquireToken } from '@core/api/http';
import { graphRequestScopes } from '@core/auth/msal.config';

/**
 * Microsoft Graph calls - the HTTP half of the old `UserService`.
 *
 * Graph is not in the protected-resource map (it takes its own `.default`
 * scope, not the portal API's), so every call here acquires its token
 * explicitly and passes it as `scopes`.
 */

const GRAPH = 'https://graph.microsoft.com/v1.0';

export interface GraphUser {
  id: string;
  displayName: string;
  mail: string;
  userPrincipalName: string;
}

interface GraphSearchResponse {
  value: GraphUser[];
}

export function getGraphToken(): Promise<string | null> {
  return acquireToken(graphRequestScopes);
}

export function getUserById(id: string): Promise<GraphUser> {
  return api.get<GraphUser>(`${GRAPH}/users/${id}`, { scopes: graphRequestScopes });
}

/**
 * Fetches many users in one round trip.
 *
 * Graph's `$batch` endpoint caps at 20 sub-requests, and the assignment
 * screens routinely resolve more appraiser ids than that, so the caller's list
 * is chunked here rather than at each call site.
 */
export async function getUsersBatch(ids: string[]): Promise<{ responses: any[] }> {
  const CHUNK = 20;
  const responses: any[] = [];

  for (let offset = 0; offset < ids.length; offset += CHUNK) {
    const chunk = ids.slice(offset, offset + CHUNK);

    const result = await api.post<{ responses: any[] }>(
      `${GRAPH}/$batch`,
      {
        requests: chunk.map((id, index) => ({
          // Ids must be unique across the whole batch, not just the chunk.
          id: String(offset + index),
          method: 'GET',
          url: `/users/${id}`
        }))
      },
      { scopes: graphRequestScopes }
    );

    responses.push(...(result?.responses ?? []));
  }

  return { responses };
}

export async function searchUsers(query: string, limit = 8): Promise<GraphUser[]> {
  const trimmed = query.trim();

  if (!trimmed) {
    return [];
  }

  const response = await api.get<GraphSearchResponse>(`${GRAPH}/users`, {
    scopes: graphRequestScopes,
    headers: {
      // `$search` on /users requires advanced query capabilities.
      ConsistencyLevel: 'eventual'
    },
    params: {
      $search: `"displayName:${trimmed}" OR "mail:${trimmed}" OR "userPrincipalName:${trimmed}"`,
      $select: 'id,displayName,mail,userPrincipalName',
      $top: limit,
      $count: 'true'
    }
  });

  return response?.value ?? [];
}

export function getUserGroups(): Promise<unknown> {
  return api.get(`${GRAPH}/me/transitiveMemberOf`, { scopes: graphRequestScopes });
}

/**
 * Group object ids for a user, via `getMemberObjects`.
 *
 * Returns `[]` on failure rather than throwing: sign-in must not dead-end
 * because Graph is unavailable, and the caller merges this with the group and
 * role claims already on the token.
 */
export async function getMemberObjects(userId: string): Promise<string[]> {
  try {
    const response = await api.post<{ value: string[] }>(
      `${GRAPH}/users/${userId}/getMemberObjects`,
      { securityEnabledOnly: false },
      { scopes: graphRequestScopes }
    );

    return response?.value ?? [];
  } catch (error) {
    console.error('Error fetching groups:', error);
    return [];
  }
}
